import type { FixingIndex, MinimalSubscriptionResponse } from '@talos/kyoko';
import { isToday } from 'date-fns';
import { useMemo } from 'react';
import { map, type Observable, shareReplay } from 'rxjs';
import {
  type CareOrderBlotterEntity,
  type CareOrderRow,
  isCareOrderRow,
  isLegRow,
  isOrderRow,
  isQuoteRow,
  type OrderRow,
  type QuoteRow,
} from '../types';

interface UseValidateFixingDataObsParams {
  careOrderBlotterEntitiesObs: Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>;
  fixingIndex: FixingIndex | undefined;
}

export const useValidateFixingDataObs = ({
  careOrderBlotterEntitiesObs,
  fixingIndex,
}: UseValidateFixingDataObsParams) => {
  const todaysEntities = useMemo(
    () =>
      careOrderBlotterEntitiesObs.pipe(
        map(msg => ({ ...msg, data: msg.data.filter(entity => isToday(new Date(entity.SubmitTime))) })),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [careOrderBlotterEntitiesObs]
  );

  const dataObservable = useMemo(() => {
    return todaysEntities.pipe(
      map(msg => {
        // 1 - filter out entities so we only have entities left that have had a Fixing applied, since that's what we want to validate in the blotter
        const fixedEntities = msg.data.filter(careOrderBlotterEntityHasBeenFixed);

        // 2 - group each child entity (customer order or quote) by care order (parent)
        const entitiesByCareOrderID = new Map<string, (QuoteRow | OrderRow)[]>();
        for (const entity of fixedEntities) {
          if (isCareOrderRow(entity) || isLegRow(entity)) {
            continue;
          }

          const careOrderID = isQuoteRow(entity) ? entity.ParentRFQID : entity.ParentOrderID;
          const relatedEntitiesList = entitiesByCareOrderID.get(careOrderID) ?? [];
          relatedEntitiesList.push(entity);
          entitiesByCareOrderID.set(careOrderID, relatedEntitiesList);
        }

        // 3 - and then based on this information, produce the output data. This resulting array is the data array sent to the blotter.
        const data = fixedEntities.filter(entity => {
          if (isCareOrderRow(entity)) {
            // only include care orders that have > 0 children put into the map
            const filteredChildCount = entitiesByCareOrderID.get(entity.OrderID)?.length ?? 0;
            if (filteredChildCount <= 0) {
              return false;
            }
          }

          if (fixingIndex != null) {
            if (isCareOrderRow(entity)) {
              // We're a care order row. When a fixing index is selected, we stay visible in the blotter if any of our kiddos have this fixing index.
              const hasChildrenWithSelectedIndex = entitiesByCareOrderID
                .get(entity.OrderID)
                ?.some(childEntity => childEntity.fixingIndices?.includes(fixingIndex.Name));
              if (!hasChildrenWithSelectedIndex) {
                return false;
              }
            } else {
              // we're a child row (quote or order), here we just check if we ourselves are of this index or not, and that we are eligible for inclusion
              if (!entity.fixingIndices?.includes(fixingIndex.Name)) {
                return false;
              }
            }
          }

          return true;
        });

        return {
          // Assumption here is that the input observable gives us an absolute data set, and has a shareReplay
          initial: true,
          data,
        };
      })
    );
  }, [todaysEntities, fixingIndex]);

  return dataObservable;
};

/**
 * Checks that the state on CustomerQuote/CustomerOrder signifies that they have had their Fixing applied.
 * This is not applicable for Care Orders, so for them we just always return true.
 */
function careOrderBlotterEntityHasBeenFixed(
  entity: CareOrderBlotterEntity
): entity is QuoteRow | OrderRow | CareOrderRow {
  // Checking that the Quote or Order respectively has moved from the PendingFix state to its respective terminal state
  if (isQuoteRow(entity)) {
    return entity.QuoteStatus === 'Filled';
  }

  if (isOrderRow(entity)) {
    return entity.OrdStatus === 'DoneForDay';
  }

  if (isLegRow(entity)) {
    return false;
  }

  return true; // include all care order rows
}
